import {
    Box,
    Grid,
    List,
    ListItem,
    ListItemButton,
    Typography,
    Tooltip,
    Divider,
    Card,
    CardContent,
} from "@mui/material";
import { AppCard } from '../../components/common/AppCard';
import { RecordContextProvider } from "react-admin";
import { useNavigate } from "react-router-dom";

// Material UI Icons
import TrendingUpIcon from "@mui/icons-material/TrendingUp"; // Positive Performance
import TrendingDownIcon from "@mui/icons-material/TrendingDown"; // Negative Performance
import LocalAtmIcon from "@mui/icons-material/LocalAtm"; // Cost Basis
import PriceChangeIcon from "@mui/icons-material/PriceChangeOutlined"; // Shares Held
import LocalOfferIcon from "@mui/icons-material/LocalOffer"; // Share Price
import ClassIcon from '@mui/icons-material/Class';
import QueryStatsIcon from "@mui/icons-material/QueryStats"; // Unrealized P&L
import PieChartIcon from "@mui/icons-material/PieChart"; // Portfolio Share
import TagIcon from "@mui/icons-material/Numbers"; // Asset Name
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"; // Portfolio Icon

import { Position } from "../../types/types";

interface Props {
    positions: Position[];
    total_portfolio_value: number;
    funds: Record<string, string>; // fundId → fundName mapping
    highlightedIds?: Set<string>;
}

export const PortfolioPositions = ({ positions, total_portfolio_value, funds }: Props) => {
    // Get user-selected currency from local storage or use default "EUR"
    const currency: "EUR" | "USD" | "CHF" =
        (localStorage.getItem("currency") as "EUR" | "USD" | "CHF") || "EUR";

    const navigate = useNavigate();

    return (
        <>
            <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <AccountBalanceWalletIcon fontSize="small" />
                Positions
            </Typography>
            <AppCard>
                <CardContent sx={{ paddingX: 0, paddingTop: 0, "&:last-child": { paddingBottom: 0 } }}>

                    {/* Column Labels */}
                    <Grid container alignItems="center" spacing={0} sx={{ paddingX: 2, paddingY: 1, backgroundColor: "action.hover" }}>
                        <Grid item xs={4}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <ClassIcon fontSize="small" /> Asset
                            </Typography>
                        </Grid>
                        <Grid item xs={2} sx={{ textAlign: "center" }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <PriceChangeIcon fontSize="small" /> Amount
                            </Typography>
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <LocalAtmIcon fontSize="small" /> Value
                            </Typography>
                        </Grid>
                        <Grid item xs={1.5} sx={{ textAlign: "right" }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <QueryStatsIcon fontSize="small" /> P&L
                            </Typography>
                        </Grid>
                        <Grid item xs={1.5} sx={{ textAlign: "right" }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 0.5 }}>
                                <QueryStatsIcon fontSize="small" /> Total P&L
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "right" }}>
                            <Typography variant="caption" sx={{ fontWeight: "bold", textTransform: "uppercase", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 0.5 }}>
                                <PieChartIcon fontSize="small" />
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Position Data */}

                    {/* Conditionally render body */}
                    {positions.length === 0 ? (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="textSecondary">
                                No positions available.
                            </Typography>
                        </Box>
                    ) : (
                        <List dense sx={{ padding: 0, margin: 0 }}>
                            {positions.map((position, index) => {
                                const portfolioShare = total_portfolio_value > 0
                                    ? ((position.value?.global?.[currency] ?? 0) / total_portfolio_value) * 100
                                    : 0;

                                const fundName = funds[position.asset_id] ?? position.asset_id; // Use fund name if available

                                return (
                                    <RecordContextProvider key={position.asset_id} value={position}>
                                        <ListItem disableGutters sx={{ padding: 0, margin: 0 }}>
                                            <ListItemButton
                                                sx={{
                                                    flexDirection: "column",
                                                    alignItems: "stretch",
                                                    paddingY: 0.75,
                                                    "&:hover": { backgroundColor: "action.hover" },
                                                }}
                                                onClick={() => navigate(`/funds/${position.asset_id}/show`)}
                                            >
                                                {/* Position Details Grid */}
                                                <Grid container alignItems="center" spacing={0} sx={{ width: "100%", padding: 0, margin: 0 }}>

                                                    {/* Asset Name */}
                                                    <Grid item xs={4}>
                                                        <Typography variant="body1" color="textPrimary" sx={{ fontWeight: "bold" }}>
                                                            {fundName}
                                                        </Typography>
                                                    </Grid>

                                                    {/* Shares / Price (Centered) */}
                                                    <Grid item xs={2} sx={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                                                        <Typography variant="body2" color="textPrimary">
                                                            {position.size.toFixed(2)}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {position.price?.global?.[currency] ? position.price.global[currency].toFixed(2) : "N/A"} {currency}
                                                        </Typography>
                                                    </Grid>

                                                    {/* Value and Cost */}
                                                    <Grid item xs={2} sx={{ display: "flex", flexDirection: "column", textAlign: "left" }}>
                                                        <Typography variant="body2" color="textPrimary">
                                                            {position.value?.global?.[currency] ? position.value.global[currency].toFixed(2) : "N/A"} {currency}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {position.cost_basis?.global?.[currency] ? position.cost_basis.global[currency].toFixed(2) : "N/A"} {currency}
                                                        </Typography>
                                                    </Grid>

                                                    {/* Unrealized P&L */}
                                                    <Grid item xs={1.5}>
                                                        <Typography
                                                            variant="body2"
                                                            color={(position.unrealized_pnl?.global?.[currency] ?? 0) > 0 ? "success.main" : (position.unrealized_pnl?.global?.[currency] ?? 0) < 0 ? "error.main" : "text.primary"}
                                                            sx={{ fontWeight: "bold" }}
                                                        >
                                                            {(position.unrealized_pnl?.global?.[currency] ?? 0) === 0
                                                                ? "-"
                                                                : `${position.unrealized_pnl.global[currency]?.toFixed(2) || "N/A"} ${currency}`
                                                            }
                                                        </Typography>
                                                    </Grid>


                                                    {/* Lifetime P&L */}
                                                    <Grid item xs={1.5}>
                                                        <Typography
                                                            variant="body2"
                                                            color={(position.lifetime_realized_pnl?.global?.[currency] ?? 0) > 0 ? "success.main" : (position.lifetime_realized_pnl?.global?.[currency] ?? 0) < 0 ? "error.main" : "text.primary"}
                                                            sx={{ fontWeight: "bold" }}
                                                        >
                                                            {(position.lifetime_realized_pnl?.global?.[currency] ?? 0) === 0
                                                                ? "-"
                                                                : `${position.lifetime_realized_pnl.global[currency]?.toFixed(2) || "N/A"} ${currency}`
                                                            }
                                                        </Typography>

                                                        {position.lifetime_watermark_pnl?.global?.[currency] !== 0 && position.lifetime_watermark_pnl?.global?.[currency] != null && (
                                                            <Typography variant="caption" color="textSecondary">
                                                                {position.lifetime_watermark_pnl?.global?.[currency]?.toFixed(2)} {currency}
                                                            </Typography>
                                                        )}
                                                    </Grid>

                                                    {/* Portfolio Share */}
                                                    <Grid item xs={1} sx={{ textAlign: "right" }}>
                                                        <Typography variant="body2" color="textPrimary">
                                                            {portfolioShare.toFixed(2)}%
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </ListItemButton>
                                        </ListItem>
                                        {index < positions.length - 1 && <Divider />}
                                    </RecordContextProvider>
                                );
                            })}
                        </List>
                    )}
                </CardContent>
            </AppCard >
        </>
    );
};
